import React, { useState, useEffect, useCallback } from 'react';
import { Box, Card, CardContent, Typography, Stack, LinearProgress } from '@mui/material';
import SubscriptionService from '../../../services/subscription.service';

const TEAL = '#0E6B6B';

// Backend wraps failures as { success:false, error:"..." }; fall back to Spring's
// default `message`, then the axios error, then the caller's fallback text.
export const err = (e, fallback) =>
  e?.response?.data?.error || e?.response?.data?.message || e?.message || fallback;

export const statusColor = (s) => (s === 'APPROVED' ? 'success' : s === 'PENDING' ? 'warning' : 'default');

/** The connection cap (providers for a promoted TC, clients for a provider) from the subscription usage. */
export function useConnectionCap(isB2b) {
  const [cap, setCap] = useState(null);

  const reloadCap = useCallback(async () => {
    try {
      const current = await SubscriptionService.getCurrent();
      const key = isB2b ? 'MAX_B2B_CLIENTS' : 'MAX_B2B_CONNECTIONS';
      const u = (current?.usage || []).find((x) => x.feature_key === key);
      if (u) {
        const used = Number(u.used || 0);
        setCap({
          used,
          limit: u.limit,
          unlimited: !!u.unlimited,
          reached: !u.unlimited && u.limit != null && used >= Number(u.limit),
        });
      } else {
        setCap(null);
      }
    } catch (e) {
      setCap(null);
    }
  }, [isB2b]);

  useEffect(() => { reloadCap(); }, [reloadCap]);

  return { cap, reloadCap };
}

/** Role/usage header — mirrors the current-plan card on /settings/subscription/usage. */
export function ConnectionsHeader({ icon, title, cap, action }) {
  const pct = cap && !cap.unlimited && cap.limit
    ? Math.min(100, Math.round((cap.used / cap.limit) * 100))
    : 0;
  const remaining = cap && !cap.unlimited ? Math.max(0, (cap.limit || 0) - (cap.used || 0)) : null;

  return (
    <Card sx={{ mb: 3, borderRadius: 2, border: '1px solid', borderColor: 'divider', borderLeft: `4px solid ${TEAL}` }}>
      <CardContent sx={{ p: 3 }}>
        <Stack direction={{ xs: 'column', sm: 'row' }} justifyContent="space-between" alignItems={{ sm: 'center' }} spacing={2}>
          <Stack direction="row" spacing={2} alignItems="center">
            <Box sx={{ width: 52, height: 52, borderRadius: 2, bgcolor: TEAL, color: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              {icon}
            </Box>
            <Box>
              <Typography variant="caption" color="text.secondary">{title}</Typography>
              <Typography variant="h5" sx={{ lineHeight: 1.2 }}>
                {cap ? (cap.unlimited ? 'Unlimited' : `${cap.used} / ${cap.limit}`) : '—'}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                {cap
                  ? (cap.unlimited ? 'No limit on your plan' : `${remaining} remaining · ${pct}% used`)
                  : ''}
              </Typography>
            </Box>
          </Stack>
          {action}
        </Stack>
        {cap && !cap.unlimited && (
          <LinearProgress
            variant="determinate"
            value={pct}
            color={pct >= 90 ? 'error' : pct >= 70 ? 'warning' : 'primary'}
            sx={{ mt: 2, height: 8, borderRadius: 4 }}
          />
        )}
      </CardContent>
    </Card>
  );
}
